import React, { useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { createAppUser, updateAppUserProfile } from '../services/firestoreService';
import Card from '../components/common/Card';
import PageHero from '../components/common/PageHero';
import Table from '../components/common/Table';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import Select from '../components/common/Select';
import Modal from '../components/common/Modal';
import RecordAuditBlock from '../components/common/RecordAuditBlock';
import {
  PERMISSION_KEYS,
  PERMISSION_LABELS,
  clerkPermissions,
  normalizeUserPermissions
} from '../utils/permissions';
import toast from 'react-hot-toast';

const ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin — dhammaan' },
  { value: 'staff', label: 'Staff — dooro qaybaha' }
];

const UserManagement = () => {
  const { user, canAccess } = useAuth();
  const auditActor = user ? { uid: user.uid, email: user.email } : null;
  const { appUsers, invoices, loadingAppUsers } = useApp();

  const invoiceCountByUid = useMemo(() => {
    const map = {};
    invoices.forEach((inv) => {
      const uid = inv.createdByUid;
      if (uid) map[uid] = (map[uid] || 0) + 1;
    });
    return map;
  }, [invoices]);

  const [showModal, setShowModal] = useState(false);
  const [detailUser, setDetailUser] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const emptyForm = {
    email: '',
    password: '',
    displayName: '',
    role: 'staff',
    permissions: clerkPermissions()
  };

  const [formData, setFormData] = useState(emptyForm);

  if (!canAccess('users')) {
    return (
      <Card>
        <p className="text-gray-600">Ma haysid ogolaanshaha boggan.</p>
      </Card>
    );
  }

  const openCreate = () => {
    setEditingUser(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditingUser(row);
    setFormData({
      email: row.email || '',
      password: '',
      displayName: row.displayName || '',
      role: row.role || 'staff',
      permissions: normalizeUserPermissions(row)
    });
    setShowModal(true);
  };

  const togglePermission = (key) => {
    if (formData.role === 'admin') return;
    setFormData((prev) => ({
      ...prev,
      permissions: { ...prev.permissions, [key]: !prev.permissions[key] }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.displayName.trim()) {
      toast.error('Magaca user-ka buuxi');
      return;
    }
    if (!editingUser && (!formData.email.trim() || !formData.password)) {
      toast.error('Email iyo password waa lagama maarmaan');
      return;
    }

    setSubmitting(true);
    let result;
    if (editingUser) {
      result = await updateAppUserProfile(
        editingUser.id,
        {
          displayName: formData.displayName,
          role: formData.role,
          permissions: formData.role === 'staff' ? formData.permissions : undefined
        },
        auditActor
      );
    } else {
      result = await createAppUser({
        email: formData.email,
        password: formData.password,
        displayName: formData.displayName,
        role: formData.role,
        permissions: formData.role === 'staff' ? formData.permissions : {},
        auditActor
      });
    }

    if (result.success) {
      toast.success(editingUser ? 'User waa la cusbooneysiiyay' : 'User cusub waa la abuuray');
      setShowModal(false);
    } else {
      toast.error(result.error || 'Khalad');
    }
    setSubmitting(false);
  };

  const handleToggleActive = async (row) => {
    if (row.id === user?.uid) {
      toast.error('Isma dami kartid account-kaaga');
      return;
    }
    const next = row.isActive === false;
    const result = await updateAppUserProfile(row.id, { isActive: next }, auditActor);
    if (result.success) toast.success(next ? 'User waa la furay' : 'User waa la xiray');
    else toast.error(result.error);
  };

  const columns = [
    {
      key: 'displayName',
      label: 'Magac',
      render: (value, row) => (
        <div>
          <p className="font-medium">{value || '—'}</p>
          <p className="text-xs text-gray-500">{row.email}</p>
        </div>
      )
    },
    {
      key: 'role',
      label: 'Role',
      render: (value) => (
        <span className={`px-2 py-0.5 rounded-full text-xs ${value === 'admin' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}`}>
          {value || 'staff'}
        </span>
      )
    },
    {
      key: 'invoiceCount',
      label: 'Invoices',
      render: (_, row) => {
        const computed = invoiceCountByUid[row.id] || 0;
        const stored = row.invoiceCount || 0;
        return (
          <span className="font-semibold text-primary-600">{Math.max(computed, stored)}</span>
        );
      }
    },
    {
      key: 'isActive',
      label: 'Xaalad',
      render: (value) => (
        <span className={value === false ? 'text-red-600' : 'text-green-600'}>
          {value === false ? 'Xiran' : 'Firfircoon'}
        </span>
      )
    },
    {
      key: 'actions',
      label: '',
      render: (_, row) => (
        <div className="flex gap-2">
          <button type="button" onClick={(e) => { e.stopPropagation(); openEdit(row); }} className="p-1 text-blue-600">✏️</button>
          <button type="button" onClick={(e) => { e.stopPropagation(); handleToggleActive(row); }} className="p-1 text-gray-600" title="Fir / xir">
            {row.isActive === false ? '🔓' : '🔒'}
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-4">
      <PageHero
        accent="red"
        eyebrow="Amniga"
        title="User Management"
        subtitle="Abuur users, dooro role iyo ogolaansho (quotation, invoice, dashboard…)"
        actions={
          <Button onClick={openCreate} icon={<span>➕</span>}>
            Abuur User
          </Button>
        }
        stats={[
          { label: 'Users', value: appUsers.length },
          { label: 'Admin', value: appUsers.filter((u) => u.role === 'admin').length },
          { label: 'Staff', value: appUsers.filter((u) => u.role !== 'admin').length },
          { label: 'Invoices (guud)', value: invoices.length }
        ]}
      />

      <Card padding={false}>
        <p className="px-4 py-2 text-xs text-gray-500 border-b">Riix saf si aad u aragto faahfaahin iyo ogolaanshaha.</p>
        <Table
          columns={columns}
          data={appUsers}
          loading={loadingAppUsers}
          emptyMessage="User lama helin"
          onRowClick={setDetailUser}
        />
      </Card>

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingUser ? 'Wax ka bedel user' : 'Abuur user cusub'}
        size="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {!editingUser && (
            <>
              <Input
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
                required
              />
              <Input
                label="Password (ugu yaraan 6)"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData((p) => ({ ...p, password: e.target.value }))}
                required
              />
            </>
          )}
          <Input
            label="Magaca muuqda"
            value={formData.displayName}
            onChange={(e) => setFormData((p) => ({ ...p, displayName: e.target.value }))}
            required
          />
          <Select
            label="Role"
            value={formData.role}
            onChange={(e) => setFormData((p) => ({ ...p, role: e.target.value }))}
            options={ROLE_OPTIONS}
          />
          {formData.role === 'staff' && (
            <div className="border rounded-lg p-3 space-y-2">
              <p className="text-sm font-medium text-gray-700">Ogolaanshaha bogagga</p>
              <div className="grid grid-cols-2 gap-2">
                {PERMISSION_KEYS.filter((k) => k !== 'users').map((key) => (
                  <label key={key} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={Boolean(formData.permissions[key])}
                      onChange={() => togglePermission(key)}
                    />
                    {PERMISSION_LABELS[key]}
                  </label>
                ))}
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer border-t pt-2">
                <input
                  type="checkbox"
                  checked={Boolean(formData.permissions.users)}
                  onChange={() => togglePermission('users')}
                />
                {PERMISSION_LABELS.users}
              </label>
            </div>
          )}
          <div className="flex gap-3 pt-2">
            <Button type="button" variant="ghost" fullWidth onClick={() => setShowModal(false)}>Ka noqo</Button>
            <Button type="submit" loading={submitting} fullWidth>{editingUser ? 'Kaydi' : 'Abuur'}</Button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={Boolean(detailUser)} onClose={() => setDetailUser(null)} title="Faahfaahinta user">
        {detailUser && (
          <div className="space-y-3 text-sm">
            <p><span className="text-gray-500">Magac:</span> {detailUser.displayName}</p>
            <p><span className="text-gray-500">Email:</span> {detailUser.email}</p>
            <p><span className="text-gray-500">Role:</span> {detailUser.role}</p>
            <p>
              <span className="text-gray-500">Invoices uu sameeyay:</span>{' '}
              {Math.max(invoiceCountByUid[detailUser.id] || 0, detailUser.invoiceCount || 0)}
            </p>
            {detailUser.role !== 'admin' && (
              <div>
                <p className="text-gray-500 mb-1">Ogolaansho:</p>
                <ul className="list-disc pl-5 text-gray-700">
                  {PERMISSION_KEYS.filter((k) => normalizeUserPermissions(detailUser)[k]).map((k) => (
                    <li key={k}>{PERMISSION_LABELS[k]}</li>
                  ))}
                </ul>
              </div>
            )}
            <RecordAuditBlock record={detailUser} />
            <Button variant="outline" onClick={() => { openEdit(detailUser); setDetailUser(null); }}>Wax ka beddel</Button>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default UserManagement;
